import React, { useState } from "react";
import { IEventSummary, IEventHotel, EventHotelDto } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import { helperService } from "src/services/helper.service";
import { calculateDrivingDistance, geocodeAddress } from "src/utils/googleMapsLoader";

import Input from "src/components/Input/Input";
import Checkbox from "src/components/Checkbox/Checkbox";
import Button from "src/components/Button/Button";
import Phone from "src/components/Phone/Phone";

import styles from "./HotelsTab.module.scss";

interface IProps {
  event: IEventSummary;
  hotel?: IEventHotel;
  onFinish(): void;
  onCancel(): void;
}

const formatAddressString = (fields: {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
}) => {
  return [fields.line1, fields.line2, fields.city, fields.state, fields.postal_code, fields.country]
    .filter((v) => !!v)
    .join(", ");
};

const HotelForm: React.FC<IProps> = (props) => {
  const [name, setName] = useState<string>(props.hotel?.name || "");
  const [line1, setLine1] = useState<string>(props.hotel?.address?.line1 || "");
  const [line2, setLine2] = useState<string>(props.hotel?.address?.line2 || "");
  const [city, setCity] = useState<string>(props.hotel?.address?.city || "");
  const [state, setState] = useState<string>(props.hotel?.address?.state || "");
  const [country, setCountry] = useState<string>(props.hotel?.address?.country || "");
  const [postalCode, setPostalCode] = useState<string>(props.hotel?.address?.postal_code || "");
  const [phone, setPhone] = useState<string>(props.hotel?.phone || "");
  const [websiteUrl, setWebsiteUrl] = useState<string>(props.hotel?.website_url || "");
  const [hasSpecialRate, setHasSpecialRate] = useState<boolean>(props.hotel?.has_special_rate || false);
  const [rate, setRate] = useState<string>(props.hotel?.rate !== undefined ? props.hotel.rate.toString() : "");
  const [saving, setSaving] = useState<boolean>(false);

  const isEditing = !!props.hotel;

  const onSubmit = async () => {
    if (!name || !line1 || !city) {
      toast.show({
        title: "Add Hotel",
        content: "Please enter a hotel name and address",
        duration: 8000,
        type: "fail"
      });
      return;
    }

    setSaving(true);

    const address = { line1, line2, city, state, country, postal_code: postalCode };
    const unit: "mi" | "km" = props.event.currency === "usd" ? "mi" : "km";

    let distance_value: number | undefined;
    let distance_unit: "mi" | "km" | undefined;
    let coordinates: { lat: number; lng: number } | undefined;

    const hotelAddress = formatAddressString({ line1, line2, city, state, country, postal_code: postalCode });

    try {
      const eventAddress = props.event.address ? formatAddressString({
        line1: props.event.address.line1 || "",
        line2: props.event.address.line2 || "",
        city: props.event.address.city || "",
        state: props.event.address.state || "",
        country: props.event.address.country || "",
        postal_code: props.event.address.postal_code || "",
      }) : "";

      if (hotelAddress && eventAddress) {
        const result = await calculateDrivingDistance(hotelAddress, eventAddress, unit);
        if (result) {
          distance_value = result.value;
          distance_unit = result.unit;
        }
      }
    } catch (e) {
      console.warn("Could not calculate distance to event:", e);
    }

    // Geocode once here (instead of every time the map loads) so the map
    // can plot this hotel without needing to re-resolve its address.
    try {
      coordinates = hotelAddress ? await geocodeAddress(hotelAddress) : undefined;
    } catch (e) {
      console.warn("Could not geocode hotel address:", e);
    }

    const dto: EventHotelDto = {
      name,
      address,
      phone: phone || undefined,
      website_url: helperService.normalizeUrl(websiteUrl),
      has_special_rate: hasSpecialRate,
      rate: hasSpecialRate && rate ? parseFloat(rate) : undefined,
      distance_value,
      distance_unit,
      coordinates,
    };

    const request = isEditing
      ? apiService.updateEventHotel(props.event._id, props.hotel!._id, dto)
      : apiService.createEventHotel(props.event._id, dto);

    request.then(() => {
      if (distance_value === undefined) {
        toast.show({
          title: "Add Hotel",
          content: `Hotel ${isEditing ? "updated" : "added"}. Couldn't calculate distance to the event — you can edit this hotel later to retry.`,
          duration: 10000,
          type: "success"
        });
      } else {
        toast.show({
          title: "Add Hotel",
          content: `Hotel successfully ${isEditing ? "updated" : "added"}`,
          duration: 6000,
          type: "success"
        });
      }
      props.onFinish();
    }).catch((e) => {
      toast.show({
        title: "Add Hotel",
        content: "Unable to save hotel",
        errorDetails: e,
        duration: 10000,
        type: "fail"
      });
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); }} className={styles.HotelForm}>
      <div className={styles.title}>{isEditing ? "Edit Hotel" : "Add Hotel"}</div>
      <div className={styles.content}>
        <Input
          label="Hotel Name"
          placeholder="Enter hotel name"
          required={true}
          defaultValue={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Address Line 1"
          placeholder="Street address"
          required={true}
          defaultValue={line1}
          onChange={(e) => setLine1(e.target.value)}
        />
        <Input
          label="Address Line 2"
          placeholder="Suite, unit, etc. (optional)"
          defaultValue={line2}
          onChange={(e) => setLine2(e.target.value)}
        />
        <div className={styles.inputRow}>
          <Input
            label="City"
            placeholder="City"
            required={true}
            defaultValue={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <Input
            label="State / Province"
            placeholder="State / Province"
            defaultValue={state}
            onChange={(e) => setState(e.target.value)}
          />
        </div>
        <div className={styles.inputRow}>
          <Input
            label="Postal / Zip Code"
            placeholder="Postal / Zip Code"
            defaultValue={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
          />
          <Input
            label="Country"
            placeholder="Country"
            defaultValue={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>
        <Phone
          label="Phone Number (optional)"
          value={phone}
          onChange={(value: string) => setPhone(value)}
        />
        <Input
          label="Website / Booking URL"
          placeholder="https://..."
          defaultValue={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
        />
        <Checkbox
          id="hasSpecialRate"
          label="Special rate available for this event"
          value={hasSpecialRate}
          onChange={(e) => setHasSpecialRate(e.target.checked)}
        />
        {hasSpecialRate && (
          <Input
            label="Rate (per night, optional)"
            placeholder="e.g. 129"
            type="number"
            defaultValue={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        )}
      </div>
      <div className={styles.actions}>
        <Button onClick={props.onCancel} variant="secondary" disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" onClick={onSubmit} isLoading={saving}>
          {isEditing ? "Save Hotel" : "Add Hotel"}
        </Button>
      </div>
    </form>
  );
};

export default HotelForm;
